import { useEffect, useState } from 'react';

/*
 * Where a heading counts as reached: the top bar (68px), the stuck page
 * header row (67px) and the band below it in which the page fades out
 * (16px). `.markdown-heading` scroll-margin-top in index.css matches, so a
 * hash-jump lands a heading exactly where this considers it reached.
 */
const HEADER_OFFSET = 151;

/**
 * The id of the heading the reader is at: the last of `headings` (from
 * extractHeadings) whose element has already reached the top of the viewport.
 */
function useActiveHeading(headings) {
  const [activeHeadingId, setActiveHeadingId] = useState('');

  useEffect(() => {
    if (headings.length === 0) {
      setActiveHeadingId('');
      return undefined;
    }

    let animationFrameId = null;

    const updateActiveHeading = () => {
      let currentHeadingId =
        headings[0].id;

      headings.forEach((heading) => {
        const element =
          document.getElementById(
            heading.id,
          );

        if (!element) {
          return;
        }

        const top =
          element.getBoundingClientRect()
            .top;

        // +2px tolerance: a hash-jump lands the heading at exactly
        // `HEADER_OFFSET` (via the matching CSS scroll-margin-top), and
        // sub-pixel scroll rounding can otherwise leave it just barely
        // past the threshold right after landing.
        if (top <= HEADER_OFFSET + 2) {
          currentHeadingId =
            heading.id;
        }
      });

      setActiveHeadingId(
        currentHeadingId,
      );

      animationFrameId = null;
    };

    const handleScroll = () => {
      if (
        animationFrameId !== null
      ) {
        return;
      }

      animationFrameId =
        window.requestAnimationFrame(
          updateActiveHeading,
        );
    };

    const initialTimer =
      window.setTimeout(
        updateActiveHeading,
        50,
      );

    window.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
      },
    );

    window.addEventListener(
      'resize',
      handleScroll,
    );

    return () => {
      window.clearTimeout(
        initialTimer,
      );

      window.removeEventListener(
        'scroll',
        handleScroll,
      );

      window.removeEventListener(
        'resize',
        handleScroll,
      );

      if (
        animationFrameId !== null
      ) {
        window.cancelAnimationFrame(
          animationFrameId,
        );
      }
    };
  }, [headings]);

  return activeHeadingId;
}

export default useActiveHeading;
